// Adressfeld mit Chips, ähnlich wie bei Linear. Komma, Semikolon, Leerzeichen
// und Zeilenumbruch machen aus dem Getippten einen Chip, Einfügen zerlegt
// ganze Listen samt Namen. Backspace im leeren Feld und ein Klick auf einen
// Chip holen die Adresse zum Korrigieren zurück ins Feld. Doppelte fasst das
// Feld still zusammen. Was an einer Adresse nicht passt, sagt problemOf, der
// Chip wird dann rot.
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { classNames } from '../helpers'
import {
  SEPARATOR,
  mergeRecipients,
  parseRecipients,
  typedRecipient
} from '../helpers/invite-recipients'

const I18N = 'components.email_chips_input.'

const COMMIT_KEYS = [',', ';', ' ']

const EmailChipsInput = ({
  id,
  label,
  placeholder,
  description,
  recipients,
  draft,
  problemOf,
  onChange,
  onBlur,
  error,
  errorAction,
  disabled,
  autoFocus
}) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)

  const errorId = `${id}-error`
  const descriptionId = `${id}-description`

  const withDraft = () =>
    draft.trim() ? mergeRecipients(recipients, [typedRecipient(draft)]) : null

  const commit = () => {
    const next = withDraft()
    if (next) onChange({ recipients: next, draft: '' })
  }

  const edit = (index) => {
    const rest = recipients.filter((_, position) => position !== index)
    const kept = draft.trim()
      ? mergeRecipients(rest, [typedRecipient(draft)])
      : rest

    onChange({ recipients: kept, draft: recipients[index].email })
    inputRef.current?.focus()
  }

  const remove = (index) => {
    onChange({
      recipients: recipients.filter((_, position) => position !== index),
      draft
    })
    inputRef.current?.focus()
  }

  const handleKeyDown = (event) => {
    if (event.nativeEvent?.isComposing) return

    // Enter macht aus der getippten Adresse einen Chip und sendet nicht.
    // Danach kommen oft noch Name, Stelle und Vorlage. ⌘/Strg+Enter lässt
    // das Feld durch, damit das Formular darauf senden kann.
    if (event.key === 'Enter') {
      if (event.metaKey || event.ctrlKey) return
      event.preventDefault()
      commit()
      return
    }

    if (COMMIT_KEYS.includes(event.key)) {
      event.preventDefault()
      commit()
      return
    }

    if (event.key === 'Backspace' && !draft && recipients.length) {
      event.preventDefault()
      edit(recipients.length - 1)
    }
  }

  // Trenner, die nicht über die Tastatur kommen (Autovervollständigen,
  // Diktat, Drag and Drop), machen genauso Chips
  const handleChange = (event) => {
    const { value } = event.target

    if (!SEPARATOR.test(value)) {
      onChange({ recipients, draft: value })
      return
    }

    const tokens = value.split(SEPARATOR)
    const rest = tokens.pop()

    onChange({
      recipients: mergeRecipients(
        recipients,
        tokens.filter(Boolean).map(typedRecipient)
      ),
      draft: rest
    })
  }

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData('text') ?? ''
    const pasted = parseRecipients(text)

    // Eine einzelne Adresse ohne Namen landet wie gewohnt als Text im Feld
    const isPlainAddress = pasted.length === 1 && !/[\s,;<]/.test(text.trim())
    if (!pasted.length || isPlainAddress) return

    event.preventDefault()
    onChange({
      recipients: mergeRecipients(withDraft() || recipients, pasted),
      draft: ''
    })
  }

  const handleBlur = (event) => {
    commit()
    onBlur?.(event)
  }

  return (
    <>
      {label && (
        <label htmlFor={id} className='block text-sm font-medium text-gray-700'>
          {label}
        </label>
      )}
      {/* relative, damit sr-only-Texte der Chips im Scrollbereich bleiben */}
      <div
        className={classNames(
          'relative flex max-h-40 w-full flex-wrap items-center gap-1.5 overflow-y-auto rounded-md border px-3 py-2 text-sm shadow-sm',
          label && 'mt-2',
          disabled ? 'bg-gray-100' : 'bg-white',
          error
            ? 'border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
            : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'
        )}
        onClick={(event) => {
          if (event.target === event.currentTarget) inputRef.current?.focus()
        }}
      >
        {recipients.map((recipient, index) => {
          const problem = problemOf(recipient, index)

          return (
            <span
              key={recipient.email}
              data-recipient={recipient.email}
              data-problem={problem ? '' : undefined}
              title={problem || undefined}
              className={classNames(
                'inline-flex max-w-full items-center gap-x-1 rounded-full py-0 pl-2.5 pr-1 leading-5',
                problem
                  ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200'
                  : 'bg-gray-100 text-gray-800'
              )}
            >
              {problem && (
                <ExclamationCircleIcon
                  className='size-3.5 shrink-0 text-red-500'
                  aria-hidden='true'
                />
              )}
              <button
                type='button'
                className='min-w-0 truncate rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
                aria-label={t(I18N + 'edit_action', {
                  email: recipient.email
                })}
                onClick={() => edit(index)}
                disabled={disabled}
              >
                {recipient.email}
              </button>
              {problem && <span className='sr-only'>{problem}</span>}
              <button
                type='button'
                className={classNames(
                  'inline-flex size-4 shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                  problem
                    ? 'text-red-400 hover:bg-red-100 hover:text-red-600'
                    : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                )}
                aria-label={t(I18N + 'remove_action', {
                  email: recipient.email
                })}
                onClick={() => remove(index)}
                disabled={disabled}
              >
                <XMarkIcon className='size-3' aria-hidden='true' />
              </button>
            </span>
          )
        })}
        {/* p-0, sonst setzt @tailwindcss/forms sein Input-Padding */}
        <input
          ref={inputRef}
          id={id}
          name={id}
          type='text'
          inputMode='email'
          autoComplete='off'
          autoCapitalize='none'
          spellCheck={false}
          value={draft}
          placeholder={recipients.length ? undefined : placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          disabled={disabled}
          autoFocus={autoFocus}
          // HeadlessUI-Dialoge setzen den Startfokus selbst über data-autofocus
          data-autofocus={autoFocus || undefined}
          aria-invalid={!!error || undefined}
          aria-describedby={
            [error && errorId, description && descriptionId]
              .filter(Boolean)
              .join(' ') || undefined
          }
          className='min-w-[12ch] flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:italic placeholder:text-gray-400 focus:ring-0'
        />
      </div>
      {error && (
        <div className='mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'>
          <p id={errorId} className='text-sm text-red-600'>
            {error}
          </p>
          {errorAction}
        </div>
      )}
      {description && (
        <p id={descriptionId} className='mt-2 text-xs italic text-gray-500'>
          {description}
        </p>
      )}
    </>
  )
}

const recipientShape = PropTypes.shape({
  email: PropTypes.string.isRequired,
  firstname: PropTypes.string,
  lastname: PropTypes.string
})

EmailChipsInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  description: PropTypes.string,
  recipients: PropTypes.arrayOf(recipientShape).isRequired,
  draft: PropTypes.string.isRequired,
  // (recipient, index) => Grund als Text oder null
  problemOf: PropTypes.func,
  // ({ recipients, draft }) => void
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  errorAction: PropTypes.node,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool
}

EmailChipsInput.defaultProps = {
  label: undefined,
  placeholder: undefined,
  description: undefined,
  problemOf: () => null,
  onBlur: undefined,
  error: undefined,
  errorAction: null,
  disabled: false,
  autoFocus: false
}

export default EmailChipsInput
